import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CalendarIcon,
  UserIcon,
  TableCellsIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import usePageTitle from '../hooks/usePageTitle';
import { getChannelReport } from '../utils/api';

const ChannelReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  usePageTitle('Channel Report Details');

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchChannel, setSearchChannel] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    channel_type: '',
    communication_type: '',
    is_active: '',
    price_type: ''
  });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getChannelReport(id);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load channel report');
      navigate('/customers/channel-report');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    if (!report?.rows) return { channelTypes: [], communicationTypes: [], priceTypes: [] };

    const channelTypes = [...new Set(report.rows.map(r => r.channel_type).filter(Boolean))].sort();
    const communicationTypes = [...new Set(report.rows.map(r => r.communication_type).filter(Boolean))].sort();
    const priceTypes = [...new Set(report.rows.map(r => r.price_type).filter(Boolean))].sort();

    return { channelTypes, communicationTypes, priceTypes };
  }, [report?.rows]);

  // Calculate total earned from all rows
  const totalEarned = useMemo(() => {
    if (!report?.rows) return 0;
    return report.rows.reduce((sum, row) => {
      const earned = parseFloat(row.earned) || 0;
      return sum + earned;
    }, 0);
  }, [report?.rows]);

  // Filter rows based on search and filters
  const filteredRows = useMemo(() => {
    if (!report?.rows) return [];

    return report.rows.filter(row => {
      // Search filter (channel name)
      if (searchChannel && !row.channel?.toLowerCase().includes(searchChannel.toLowerCase())) {
        return false;
      }

      // Dropdown filters
      if (filters.channel_type && row.channel_type !== filters.channel_type) {
        return false;
      }
      if (filters.communication_type && row.communication_type !== filters.communication_type) {
        return false;
      }
      if (filters.is_active !== '' && String(row.is_active) !== filters.is_active) {
        return false;
      }
      if (filters.price_type && row.price_type !== filters.price_type) {
        return false;
      }

      return true;
    });
  }, [report?.rows, searchChannel, filters]);

  // Calculate total earned from filtered rows
  const filteredTotalEarned = useMemo(() => {
    return filteredRows.reduce((sum, row) => {
      const earned = parseFloat(row.earned) || 0;
      return sum + earned;
    }, 0);
  }, [filteredRows]);

  const hasActiveFilters = searchChannel || Object.values(filters).some(v => v !== '');

  const clearFilters = () => {
    setSearchChannel('');
    setFilters({
      channel_type: '',
      communication_type: '',
      is_active: '',
      price_type: ''
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num % 1 === 0 ? num.toLocaleString() : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return `${num.toFixed(2)}%`;
  };

  const formatBoolean = (value) => {
    if (value === null || value === undefined) return '-';
    return value ? 'Yes' : 'No';
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner" />
        <span>Loading report...</span>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">Report not found</div>
        <p className="page-subtitle">The requested channel report could not be found</p>
      </div>
    );
  }

  // Column definitions for the data table
  const columns = [
    { key: 'row_number', label: '#', sticky: false },
    { key: 'channel', label: 'Channel', sticky: true },
    { key: 'customer_name', label: 'Client' },
    { key: 'channel_type', label: 'Channel Type' },
    { key: 'communication_type', label: 'Communication' },
    { key: 'pos', label: 'Pos.' },
    { key: 'is_active', label: 'Active', format: 'boolean' },
    { key: 'price_type', label: 'Price Type' },
    { key: 'invalid', label: 'Invalid', format: 'number' },
    { key: 'missing_data', label: 'Missing Data', format: 'number' },
    { key: 'rejected', label: 'Rejected', format: 'number' },
    { key: 'sent', label: 'Sent', format: 'number' },
    { key: 'error', label: 'Error', format: 'number' },
    { key: 'duplicate', label: 'Duplicate', format: 'number' },
    { key: 'paused', label: 'Paused', format: 'number' },
    { key: 'not_verified', label: 'Not Verified', format: 'number' },
    { key: 'leads', label: 'Leads', format: 'number' },
    { key: 'sold_or_paused', label: 'Sold/Paused', format: 'number' },
    { key: 'redirected', label: 'Redirected', format: 'number' },
    { key: 'landed', label: 'Landed', format: 'number' },
    { key: 'full_app', label: 'Full App', format: 'number' },
    { key: 'loan_issued', label: 'Loan Issued', format: 'number' },
    { key: 'loan_rejected', label: 'Loan Rejected', format: 'number' },
    { key: 'tier_1', label: 'Tier 1', format: 'number' },
    { key: 'tier_2', label: 'Tier 2', format: 'number' },
    { key: 'tier_3', label: 'Tier 3', format: 'number' },
    { key: 'tier_4', label: 'Tier 4', format: 'number' },
    { key: 'tier_5', label: 'Tier 5', format: 'number' },
    { key: 'conversion', label: 'Conversion', format: 'percent' },
    { key: 'conversion_sms', label: 'Conv. (SMS)', format: 'percent' },
    { key: 'total_conversion', label: 'Total Conv.', format: 'percent' },
    { key: 'avg_price_paused', label: 'Avg Price (Paused)', format: 'number' },
    { key: 'avg_price_paused_or_sold', label: 'Avg Price (P/S)', format: 'number' },
    { key: 'avg_price_lead', label: 'Avg Price (Lead)', format: 'number' },
    { key: 'ecpc', label: 'eCPC', format: 'number' },
    { key: 'ecpa', label: 'eCPA', format: 'number' },
    { key: 'ecps', label: 'eCPS', format: 'number' },
    { key: 'earned', label: 'Earned', format: 'number' }
  ];

  const formatCellValue = (row, col) => {
    const value = row[col.key];
    if (col.format === 'number') return formatNumber(value);
    if (col.format === 'percent') return formatPercent(value);
    if (col.format === 'boolean') return formatBoolean(value);
    return value || '-';
  };

  // Calculate left position for sticky columns
  const getStickyStyle = (col, index) => {
    if (!col.sticky) return {};

    // Calculate left offset based on previous columns
    let leftOffset = 0;
    for (let i = 0; i < index; i++) {
      leftOffset += 50; // Approximate width of # column
    }

    return {
      position: 'sticky',
      left: leftOffset,
      zIndex: 2,
      background: 'var(--color-bg-secondary)',
      boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
    };
  };

  const getStickyHeaderStyle = (col, index) => {
    if (!col.sticky) return {};

    let leftOffset = 0;
    for (let i = 0; i < index; i++) {
      leftOffset += 50;
    }

    return {
      position: 'sticky',
      left: leftOffset,
      zIndex: 12,
      background: 'var(--color-bg-tertiary)',
      boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
    };
  };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/customers/channel-report')}
          >
            <ArrowLeftIcon style={{ width: 18, height: 18 }} />
            Back
          </button>
          <div>
            <h1 className="page-title">Channel Report Details</h1>
            <p className="page-subtitle">
              {formatDate(report.from_date)} - {formatDate(report.to_date)}
            </p>
          </div>
        </div>
      </div>

      {/* Report Info Cards */}
      <div className="stats-grid" style={{ marginBottom: 'var(--space-lg)' }}>
        <div className="stat-card">
          <div className="stat-icon blue">
            <CalendarIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Period</div>
            <div className="stat-value" style={{ fontSize: '1rem' }}>
              {formatDate(report.from_date)}
            </div>
            <div className="stat-subtext">to {formatDate(report.to_date)}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <TableCellsIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Rows</div>
            <div className="stat-value">{report.row_count}</div>
            <div className="stat-subtext">
              {filteredRows.length !== report.row_count
                ? `Showing ${filteredRows.length} filtered`
                : 'Data entries imported'}
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <UserIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Uploaded By</div>
            <div className="stat-value" style={{ fontSize: '1rem' }}>
              {report.uploaded_by_name || '-'}
            </div>
            <div className="stat-subtext">User who uploaded</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon yellow">
            <ClockIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Upload Date</div>
            <div className="stat-value" style={{ fontSize: '1rem' }}>
              {formatDateTime(report.uploaded_at)}
            </div>
            <div className="stat-subtext">When report was uploaded</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <BanknotesIcon style={{ width: 24, height: 24 }} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Earned</div>
            <div className="stat-value">
              {totalEarned.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CZK
            </div>
            <div className="stat-subtext">
              {hasActiveFilters && filteredTotalEarned !== totalEarned
                ? `Filtered: ${filteredTotalEarned.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CZK`
                : 'Sum of all earned values'}
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Report Data</h2>
          <span style={{ color: 'var(--color-text-tertiary)', fontSize: '0.9rem' }}>
            {filteredRows.length} of {report.rows?.length || 0} rows
          </span>
        </div>

        {/* Search and Filters */}
        <div className="search-bar" style={{ marginBottom: 'var(--space-md)' }}>
          <div className="search-input-wrapper">
            <MagnifyingGlassIcon className="search-icon" style={{ width: 18, height: 18 }} />
            <input
              type="text"
              className="form-input search-input"
              placeholder="Search by channel name..."
              value={searchChannel}
              onChange={(e) => setSearchChannel(e.target.value)}
            />
          </div>
          <button
            className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon style={{ width: 18, height: 18 }} />
            Filters {hasActiveFilters && <span style={{ marginLeft: 4 }}>•</span>}
          </button>
        </div>

        {showFilters && (
          <div style={{ marginBottom: 'var(--space-lg)', padding: 'var(--space-md)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <div className="filters-row">
              <div className="filter-group">
                <label>Channel Type</label>
                <select
                  className="form-select"
                  value={filters.channel_type}
                  onChange={(e) => setFilters({ ...filters, channel_type: e.target.value })}
                >
                  <option value="">All types</option>
                  {filterOptions.channelTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Communication</label>
                <select
                  className="form-select"
                  value={filters.communication_type}
                  onChange={(e) => setFilters({ ...filters, communication_type: e.target.value })}
                >
                  <option value="">All</option>
                  {filterOptions.communicationTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Active</label>
                <select
                  className="form-select"
                  value={filters.is_active}
                  onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Price Type</label>
                <select
                  className="form-select"
                  value={filters.price_type}
                  onChange={(e) => setFilters({ ...filters, price_type: e.target.value })}
                >
                  <option value="">All</option>
                  {filterOptions.priceTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  className="btn"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  style={{
                    background: hasActiveFilters ? 'var(--color-accent-warning)' : 'var(--color-bg-elevated)',
                    borderColor: hasActiveFilters ? 'var(--color-accent-warning)' : 'var(--color-border-primary)',
                    color: hasActiveFilters ? 'white' : 'var(--color-text-tertiary)',
                    cursor: hasActiveFilters ? 'pointer' : 'not-allowed'
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="table-container" style={{ overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
          <table className="table" style={{ minWidth: '2500px' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--color-bg-tertiary)' }}>
              <tr>
                {columns.map((col, index) => (
                  <th key={col.key} style={{ whiteSpace: 'nowrap', ...getStickyHeaderStyle(col, index) }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.length > 0 ? (
                filteredRows.map((row) => (
                  <tr key={row.id}>
                    {columns.map((col, index) => (
                      <td key={col.key} style={{ whiteSpace: 'nowrap', ...getStickyStyle(col, index) }}>
                        {col.key === 'channel' ? (
                          <strong>{formatCellValue(row, col)}</strong>
                        ) : (
                          formatCellValue(row, col)
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--color-text-tertiary)' }}>
                    {hasActiveFilters ? 'No rows match the current filters' : 'No data rows found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ChannelReportDetail;
